import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { SIDEBAR_NAV } from '../../constants';
import type { Screen } from '../../types';

interface Props { forceCollapsed?: boolean }

export default function Sidebar({ forceCollapsed }: Props) {
  const { screen, go, currentUser, logout } = useApp();
  const [collapsed, setCollapsed] = useState(false);

  // Sync with forceCollapsed prop (tablet mode)
  useEffect(() => {
    if (forceCollapsed) setCollapsed(true);
  }, [forceCollapsed]);

  const isCollapsed = collapsed || !!forceCollapsed;

  return (
    <div style={{
      width: isCollapsed ? '54px' : '220px',
      background: '#0F1B2D',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'width .25s',
      height: '100vh',
    }}>
      {/* Brand */}
      <div style={{
        padding: isCollapsed ? '14px 0' : '14px 14px 12px',
        display: 'flex', alignItems: 'center', gap: '10px',
        borderBottom: '1px solid rgba(255,255,255,.07)',
        flexShrink: 0,
        justifyContent: isCollapsed ? 'center' : undefined,
      }}>
        <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#C8973A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: '#0F1B2D', flexShrink: 0 }}>e</div>
        {!isCollapsed && (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>SalesBook</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.4)', marginTop: '1px' }}>{currentUser?.title}</div>
            </div>
            {!forceCollapsed && (
              <button onClick={() => setCollapsed(true)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontSize: '14px', flexShrink: 0, padding: '2px', lineHeight: 1 }}>
                <i className="ti ti-layout-sidebar-left-collapse" />
              </button>
            )}
          </>
        )}
        {isCollapsed && !forceCollapsed && (
          <div style={{ position: 'absolute' }} />
        )}
      </div>

      {/* Expand button (only when user-collapsed, not force-collapsed) */}
      {isCollapsed && !forceCollapsed && (
        <button onClick={() => setCollapsed(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontSize: '14px', padding: '10px 0', display: 'flex', justifyContent: 'center', flexShrink: 0, transition: 'color .15s' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,.7)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,.3)')}>
          <i className="ti ti-layout-sidebar-left-expand" />
        </button>
      )}

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 0' }}>
        {!isCollapsed && (
          <div style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,.35)', padding: '10px 16px 4px', whiteSpace: 'nowrap' }}>Main</div>
        )}
        {SIDEBAR_NAV.map(item => {
          const isActive = screen === item.screen;
          return (
            <button key={item.id} onClick={() => go(item.screen as Screen)}
              title={isCollapsed ? item.label : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: isCollapsed ? 0 : '10px',
                padding: isCollapsed ? '10px 0' : '8px 16px',
                cursor: 'pointer', transition: 'all .15s', position: 'relative',
                color: isActive ? '#E8B55A' : 'rgba(255,255,255,.6)',
                fontSize: '12px', fontWeight: '500', border: 'none',
                background: isActive ? 'rgba(200,151,58,.15)' : 'none',
                fontFamily: 'inherit', width: '100%',
                textAlign: isCollapsed ? 'center' : 'left',
                justifyContent: isCollapsed ? 'center' : undefined,
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'none'; }}
            >
              {isActive && (
                <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: '3px', height: '20px', background: '#C8973A', borderRadius: '0 3px 3px 0' }} />
              )}
              <i className={`ti ${item.icon}`} style={{ fontSize: '16px', flexShrink: 0, width: '18px', textAlign: 'center' }} />
              {!isCollapsed && <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
              {!isCollapsed && item.badge && (
                <span style={{ background: item.badgeRed ? '#EF4444' : '#C8973A', color: item.badgeRed ? 'white' : '#0F1B2D', fontSize: '9px', fontWeight: '700', borderRadius: '20px', padding: '1px 7px', flexShrink: 0 }}>
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User footer */}
      <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(255,255,255,.07)', flexShrink: 0 }}>
        <div onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px', borderRadius: '8px', justifyContent: isCollapsed ? 'center' : undefined, transition: 'background .15s' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.06)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#C8973A', color: '#0F1B2D', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {currentUser?.ini}
          </div>
          {!isCollapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUser?.name}</div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,.35)' }}>Click to sign out</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
